import type { ProjectView } from '@/types/current-project';
import { executionMilestones } from '@/lib/project-presentation';
import { useProjectParticipants } from '@/hooks/useProjectViews';
import { TeamMemberCard } from './TeamMemberCard';
import { Button } from '@/components/ui';
import { useSkillsAndRoles } from '@/hooks/useSkillsAndRoles';
import { useAuthStore } from '@/stores/authStore';
import { useDaoConfig } from '@/hooks/useDaoConfig';

export function ProjectTeamList({ project }: { project: ProjectView }) {
  const { getRoleName } = useSkillsAndRoles();
  const { workerRole } = useDaoConfig();
  const scope =
    project.proposals.find((p) => p.status === 'Approved') ??
    [...project.proposals].reverse().find((p) => p.status !== 'Cancelled');
  const unassigned =
    scope?.milestones.flatMap((m) =>
      m.definition.requirements
        .filter((r) => !m.assignments.some((a) => a.requirementKey === r.key))
        .map((r) => ({
          key: m.definition.key + ':' + r.key,
          role: getRoleName(r.roleId) ?? 'Role #' + r.roleId,
        }))
    ) ?? [];
  const participants = useProjectParticipants(project.projectId);
  const account = useAuthStore((state) => state.session?.accountId);
  const workers = [
    ...new Set(
      executionMilestones(project).flatMap((milestone) =>
        milestone.assignments.map((assignment) => assignment.worker)
      )
    ),
  ];
  return (
    <section className="space-y-[--spacing-10] text-left">
      <h2 className="text-xl font-semibold">Team</h2>
      {participants.isPending && <p role="status">Loading team…</p>}
      {participants.isError && (
        <div role="alert">
          <p>Could not load team details.</p>
          <Button
            variant="secondary"
            onClick={() => void participants.refetch()}
          >
            Retry team
          </Button>
        </div>
      )}
      {participants.data && (
        <TeamMemberCard
          participant={participants.data.coordinator}
          isCurrentUser={project.coordinator === account}
          roleName="Coordinator"
        />
      )}
      {workers.map((id) => {
        const worker = participants.data?.workers.find(
          (p) => p.accountId === id
        );
        const roles = [
          ...new Set(
            executionMilestones(project).flatMap((milestone) =>
              milestone.assignments
                .filter((a) => a.worker === id)
                .map((a) => {
                  const requirement = milestone.definition.requirements.find(
                    (r) => r.key === a.requirementKey
                  );
                  return requirement
                    ? (getRoleName(requirement.roleId) ??
                        'Role #' + requirement.roleId)
                    : workerRole;
                })
            )
          ),
        ];
        return worker ? (
          <TeamMemberCard
            key={id}
            participant={worker}
            isCurrentUser={id === account}
            roleName={roles.join(' · ')}
          />
        ) : (
          <p
            key={id}
            className="break-all text-sm text-[--text-dark-secondary]"
          >
            Assigned worker: {id}
          </p>
        );
      })}
      {unassigned.map((slot) => (
        <TeamMemberCard key={slot.key} roleName={slot.role} />
      ))}
      {!workers.length && !unassigned.length && (
        <p className="text-sm text-muted-foreground">
          No execution workers assigned yet.
        </p>
      )}
    </section>
  );
}
